import { useEffect, useRef } from "react";
import { trpc } from "@/lib/trpc";
import { useAuth } from "@/_core/hooks/useAuth";

const REFERRAL_CODE_KEY = "gp_referral_code";

/**
 * Hook that captures ?ref=CODE from the URL on first visit,
 * stores it in localStorage, and auto-claims it after the user logs in.
 *
 * Flow:
 *   1. On any page load, check URL for ?ref=CODE
 *   2. If found, store in localStorage and clean the URL
 *   3. After login, if a stored code exists, call claimReferral
 *   4. On success or "already referred", clear the stored code
 */
export function useReferralCapture() {
  const { user, isAuthenticated } = useAuth();
  const claimMutation = trpc.referralCode.claimReferral.useMutation();
  const hasClaimed = useRef(false);

  // Step 1: Capture ref code from URL
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const refCode = params.get("ref");
    if (refCode) {
      localStorage.setItem(REFERRAL_CODE_KEY, refCode.toUpperCase());
      // Clean the URL without reload
      params.delete("ref");
      const newUrl = params.toString()
        ? `${window.location.pathname}?${params.toString()}`
        : window.location.pathname;
      window.history.replaceState({}, "", newUrl);
    }
  }, []);

  // Step 2: Auto-claim after login
  useEffect(() => {
    if (!isAuthenticated || !user || hasClaimed.current) return;

    const storedCode = localStorage.getItem(REFERRAL_CODE_KEY);
    if (!storedCode) return;

    hasClaimed.current = true;
    claimMutation.mutate(
      { code: storedCode },
      {
        onSuccess: (result) => {
          if (result.success || result.error === "You have already been referred") {
            localStorage.removeItem(REFERRAL_CODE_KEY);
          }
          // If code is invalid, also clear to avoid retrying forever
          if (result.error === "Invalid referral code") {
            localStorage.removeItem(REFERRAL_CODE_KEY);
          }
        },
        onError: () => {
          // Allow retry on next page load
          hasClaimed.current = false;
        },
      }
    );
  }, [isAuthenticated, user]);
}

/**
 * Get the stored referral code (if any) without claiming it.
 * Useful for showing "Referred by X" on the signup page.
 */
export function getStoredReferralCode(): string | null {
  return localStorage.getItem(REFERRAL_CODE_KEY);
}
